import snakecaseKeys from 'snakecase-keys';

import { restClient } from '../../client';
import { getApiErrorMessage, isApiError } from '../../errors';


export enum AgreementTermType {
  days = 'days',
  months = 'months',
  years = 'years',
}

export enum AgreementTemplateType {
  none = 'none',
  collaborativePracticeAgreement = 'collaborative_practice_agreement',
  stateExhibit = 'state_exhibit',
}

export type AgreementTemplate = {
  id: string;
  type: AgreementTemplateType;
  name: string;
  active: boolean;
  description: string;
  stateIds: string[];
  version: number | null;
  versionUpdatedAt: string;
  agreementTermType: AgreementTermType;
  agreementTermValue: number;
  renewalReminderLeadDays: number;
  gracePeriodAfterExpirationDays: number;
  reminderFrequencyDaysDuringGracePeriod: number;
  requireBothPartiesToResignOnRenewal: boolean;
  allowPdfUploadAsFallbackToPlatformSigning: boolean;
  createdAt: string;
  updatedAt: string | null;
};


export type AgreementTemplateVersion = {
  templateId: string;
  version: number;
  content: string;
  createdAt: string;
  updatedAt: string;
};


export const getAllAgreementTemplates = () => restClient.request<{ data: AgreementTemplate[] }>(
  '/api/v1/admin/agreement-template'
);


export type AgreementTemplateTokenGroup = 'provider' | 'collab';

export type AgreementTemplateToken = {
  value: string;
  label: string;
  group: AgreementTemplateTokenGroup;
};

export const getAgreementTemplateTokens = () => restClient.request<{ data: AgreementTemplateToken[] }>(
  '/api/v1/admin/agreement-template/tokens'
);


export const getAgreementTemplatesByIds = (ids: string[]) => restClient.request<{ data: AgreementTemplate[] }>(
  '/api/v1/agreement-template/by_ids',
  {
    method: 'POST',
    data: { ids: ids },
  }
);


export const getAgreementTemplatePreview = (templateId: string, matchId?: string) => restClient.request<{
  data: {
    template: AgreementTemplate;
    version: AgreementTemplateVersion;
  };
}>(
  `/api/v1/agreement-template/${templateId}`,
  { params: matchId ? { match_id: matchId } : undefined }
);


export const getAgreementTemplateVersions = (templateId: string) => restClient.request<{ data: AgreementTemplateVersion[] }>(
  `/api/v1/admin/agreement-template/${templateId}/versions`
);


export const createAgreementTemplate = (payload: { name: string }) =>
  restClient.request<{ data: string }>('/api/v1/admin/agreement-template', {
    method: 'POST',
    data: snakecaseKeys(payload),
  });


export type AgreementTemplateMutablePayload = {
  type: AgreementTemplateType;
  name: string;
  description: string;
  stateIds: string[];
  agreementTermType: AgreementTermType;
  agreementTermValue: number;
  renewalReminderLeadDays: number;
  gracePeriodAfterExpirationDays: number;
  reminderFrequencyDaysDuringGracePeriod: number;
  requireBothPartiesToResignOnRenewal: boolean;
  allowPdfUploadAsFallbackToPlatformSigning: boolean;
};

export const updateAgreementTemplate = (templateId: string, payload: AgreementTemplateMutablePayload) =>
  restClient.request<{ data: AgreementTemplate }>(`/api/v1/admin/agreement-template/${templateId}`, {
    method: 'PUT',
    data: snakecaseKeys(payload),
  });


export const createAgreementTemplateVersion = (templateId: string, content: string) =>
  restClient.request<{ data: number }>(`/api/v1/admin/agreement-template/${templateId}/versions`, {
    method: 'POST',
    data: { content },
  });

export const updateAgreementTemplateVersionContent = (templateId: string, version: number, content: string) =>
  restClient.request(`/api/v1/admin/agreement-template/${templateId}/versions/${version}`, {
    method: 'PUT',
    data: { content },
  });


export type PublishErrorBody = {
  message?: string;
  tokens?: string[];
};

export const publishAgreementTemplateVersion = (templateId: string, version: number) =>
  restClient.request<{ data: null }>(`/api/v1/admin/agreement-template/${templateId}/versions/${version}/publish`, {
    method: 'POST',
  });

export const publishErrorMessage = (error: unknown): string => {
  if (isApiError<{ error?: string | PublishErrorBody } | undefined>(error)) {
    const data = error.response?.data;

    if (data?.error && typeof data.error === 'object') {
      const { message, tokens } = data.error;

      if (tokens?.length) {
        return `${message ?? 'Unsupported template variable(s)'}: ${tokens.join(', ')}`;
      }
      return message ?? 'Failed to publish';
    }
  }
  return getApiErrorMessage(error) ?? 'Failed to publish';
};
